import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'antd'
import {
  filter,
  flatten,
  get,
  groupBy,
  includes,
  intersection,
  isEmpty,
  isNil,
  map,
  size,
  uniq,
} from 'lodash'
import PropTypes from 'prop-types'

import { uniquePrimitives } from '../../../core/utils'

import { INGREDIENT_AUTOCHECK_TREE_HEIGHT } from '../../constants'
import {
  groupOwnerIngredientsByComponentIngredientId,
  recursivelyGetOwnerIngredientNodes,
} from '../../helpers'
import { idPropType, nodePropType } from '../../prop-types'
import { flattenTreeStructure } from '../../utils'
import { StyledVerticalSpace } from '../styled'

import IngredientAutocheckTreeTitle from './ingredient-autocheck-tree-title'
import SearchableMetadataTree from './searchable-metadata-tree'

const IngredientsAutocheckTree = ({
  treeData,
  ingredientsFromOtherSources,
  value,
  onChange,
  includeComponentIngredients,
  disabled,
}) => {
  const [treeExpandedKeys, setTreeExpandedKeys] = useState([])
  const [treeCheckedKeys, setTreeCheckedKeys] = useState([])
  const [treeNodeIdsToExclude, setTreeNodeIdsToExclude] = useState([])
  const flatTreeData = useMemo(() => flattenTreeStructure(treeData), [treeData])

  const groupedFlatTreeDataById = useMemo(
    () => groupBy(flatTreeData, 'id'),
    [flatTreeData],
  )

  const ownerIngredientsGroupedByComponentIngredientId = useMemo(
    () =>
      includeComponentIngredients
        ? groupOwnerIngredientsByComponentIngredientId(flatTreeData)
        : [],
    [flatTreeData, includeComponentIngredients],
  )

  const groupedIngredientsFromOtherSourcesById = useMemo(
    () => groupBy(ingredientsFromOtherSources, 'id'),
    [ingredientsFromOtherSources],
  )

  useEffect(() => {
    if (!includeComponentIngredients) {
      const checkedNodes = map(value, id =>
        get(groupedFlatTreeDataById, `${id}[0]`),
      )

      setTreeCheckedKeys(uniq(map(checkedNodes, 'key')))
      setTreeExpandedKeys(uniq(flatten(map(checkedNodes, 'keyPath'))))
      return
    }

    const checkedIngredientIds = [
      ...value,
      ...map(ingredientsFromOtherSources, 'id'),
    ]

    const checkedNodes = map(checkedIngredientIds, id =>
      get(groupedFlatTreeDataById, `${id}[0]`),
    )

    const nodesToExclude = recursivelyGetOwnerIngredientNodes({
      ingredientIds: checkedIngredientIds,
      ownerIngredientsGroupedByComponentIngredientId,
      result: checkedNodes,
    })

    const nodesToCheck = filter(
      nodesToExclude,
      ({ type, originalTypeOfWhole }) => {
        if (isNil(type)) {
          return true
        }

        if (!isNil(originalTypeOfWhole) && originalTypeOfWhole === 'PROBABLE') {
          return false
        }

        return type === 'DEFINITE'
      },
    )

    setTreeNodeIdsToExclude(uniquePrimitives(map(nodesToExclude, 'id')))
    setTreeCheckedKeys(uniq(map(nodesToCheck, 'key')))
    setTreeExpandedKeys(uniq(flatten(map(nodesToCheck, 'keyPath'))))
  }, [
    value,
    flatTreeData,
    groupedFlatTreeDataById,
    ownerIngredientsGroupedByComponentIngredientId,
    ingredientsFromOtherSources,
    includeComponentIngredients,
  ])

  const handleTreeOnExpand = useCallback(expandedKeys => {
    setTreeExpandedKeys(expandedKeys)
  }, [])

  const handleTreeOnCheck = useCallback(
    ({ checked }, { node: pressedNode }) => {
      const shouldRemoveCheckedNodes = size(checked) < size(treeCheckedKeys)

      if (shouldRemoveCheckedNodes) {
        const nodesToCheck = filter(flatTreeData, ({ key }) =>
          includes(checked, key),
        )

        return onChange(map(nodesToCheck, 'id'))
      }

      const nodesToCheck = filter(flatTreeData, ({ key, keyPath }) => {
        if (includeComponentIngredients) {
          const shouldCheckChildren =
            !isEmpty(pressedNode?.children) &&
            includes(keyPath, pressedNode.key)

          if (shouldCheckChildren) {
            return (
              includes(checked, key) || !isEmpty(intersection(checked, keyPath))
            )
          }

          return includes(checked, key)
        }

        return includes(checked, key)
      })

      return onChange(map(nodesToCheck, 'id'))
    },
    [flatTreeData, includeComponentIngredients, onChange, treeCheckedKeys],
  )

  const renderTreeNode = useCallback(
    (node, searchValue) => (
      <IngredientAutocheckTreeTitle
        node={node}
        searchValue={searchValue}
        includeComponentIngredients={includeComponentIngredients}
        groupedFlatTreeDataById={groupedFlatTreeDataById}
        groupedIngredientsFromOtherSourcesById={
          groupedIngredientsFromOtherSourcesById
        }
        treeCheckedKeys={treeCheckedKeys}
        treeNodeIdsToExclude={treeNodeIdsToExclude}
      />
    ),
    [
      groupedFlatTreeDataById,
      groupedIngredientsFromOtherSourcesById,
      includeComponentIngredients,
      treeCheckedKeys,
      treeNodeIdsToExclude,
    ],
  )

  return (
    <StyledVerticalSpace>
      <Row justify="space-between">
        <Col span={24}>
          <SearchableMetadataTree
            showSearch
            checkable
            disabled={disabled}
            checkStrictly
            checkedKeys={treeCheckedKeys}
            onCheck={handleTreeOnCheck}
            onExpand={handleTreeOnExpand}
            expandedKeys={treeExpandedKeys}
            titleRender={renderTreeNode}
            treeData={treeData}
            blockNode
            flatTreeData={flatTreeData}
            height={INGREDIENT_AUTOCHECK_TREE_HEIGHT}
          />
        </Col>
      </Row>
    </StyledVerticalSpace>
  )
}

IngredientsAutocheckTree.propTypes = {
  value: PropTypes.arrayOf(idPropType.isRequired),
  onChange: PropTypes.func,
  treeData: PropTypes.arrayOf(nodePropType),
  ingredientsFromOtherSources: PropTypes.arrayOf(
    PropTypes.shape({
      id: idPropType.isRequired,
      source: PropTypes.string.isRequired,
      sourceBadgeType: PropTypes.oneOf(['warning']).isRequired,
    }),
  ),
  disabled: PropTypes.bool,
  includeComponentIngredients: PropTypes.bool,
}
IngredientsAutocheckTree.defaultProps = {
  value: [],
  onChange: undefined,
  treeData: [],
  ingredientsFromOtherSources: [],
  disabled: false,
  includeComponentIngredients: false,
}

export default IngredientsAutocheckTree
